/**
 * The waitlist form — Mouja's only acquisition mechanism while the product is
 * pre-launch.
 *
 * Two fields, deliberately. Every extra one costs signups, and everything a
 * role or category question would tell us is inferable from the store URL. No
 * account is created and nothing is sent.
 *
 * **The success panel appears only after the server confirms acceptance.** The
 * design package's version sets `done` synchronously in the submit handler,
 * which shows "You're on the list" to somebody whose request never left the
 * building. That is exactly the false progress the whole project is written
 * against, so it is not preserved here: `status` moves to `success` on an `ok`
 * outcome and on nothing else.
 */

import { useEffect, useId, useRef, useState, type SyntheticEvent } from 'react';
import { ArrowRight, Check, Link as LinkIcon, Mail } from 'lucide-react';

import { useLanguage } from '../i18n/language.js';
import { useMediaQuery } from '../hooks/useMediaQuery.js';
import { submitWaitlist } from '../lib/waitlist.js';

/**
 * The same conservative shape the server applies, so an obvious typo is caught
 * without a round trip. It is not a second source of truth: the server
 * re-validates everything and its refusal is what the visitor is finally told.
 */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

type Status = 'idle' | 'submitting' | 'success';

const WAITLIST_PANEL_RADIUS = 'var(--radius-sm)';
const WAITLIST_CONTROL_RADIUS = 'var(--radius-2xs)';

function Field({
  id,
  label,
  hint,
  error,
  icon: Glyph,
  wide,
  ...input
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
  readonly icon: typeof Mail;
  readonly wide: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>): React.ReactElement {
  const [focused, setFocused] = useState(false);
  const errorId = `${id}-error`;

  return (
    <div
      style={{
        flex: wide ? '1 1 100%' : '1 1 220px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
      }}
    >
      <label
        htmlFor={id}
        style={{
          font: 'var(--type-caption)',
          letterSpacing: 'var(--type-caption-tracking)',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
        {hint === undefined ? null : (
          <span style={{ color: 'var(--text-muted)' }}> ({hint})</span>
        )}
      </label>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          height: 46,
          padding: '0 14px',
          background: 'var(--surface-card)',
          border: `1px solid ${
            error === undefined
              ? focused
                ? 'var(--border-focus)'
                : 'var(--border-input)'
              : 'var(--status-error-fg)'
          }`,
          borderRadius: WAITLIST_CONTROL_RADIUS,
          transform: focused ? 'translateY(-1px)' : 'none',
          boxShadow:
            focused && error === undefined
              ? 'var(--inset-field-focus)'
              : 'var(--inset-field)',
          transition:
            'box-shadow var(--duration-base) var(--ease-standard), transform var(--duration-base) var(--ease-standard), border-color var(--duration-base) var(--ease-standard)',
        }}
      >
        <Glyph
          size={15}
          strokeWidth={1.5}
          color={focused ? 'var(--action-primary)' : 'var(--text-muted)'}
          aria-hidden="true"
        />
        <input
          {...input}
          id={id}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : errorId}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
          className="mj-ltr mj-waitlist-input"
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            font: 'var(--type-body-md)',
            fontFamily: 'inherit',
            color: 'var(--text-primary)',
            caretColor: 'var(--mj-action-magenta)',
            fontFeatureSettings: 'var(--font-feature-default)',
          }}
        />
      </span>
      {error === undefined ? null : (
        <span
          id={errorId}
          role="alert"
          style={{
            font: 'var(--type-caption)',
            color: 'var(--status-error-fg)',
            paddingInlineStart: 14,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

export function WaitlistForm({
  defaultStoreUrl,
  embedded = false,
}: {
  readonly defaultStoreUrl: string;
  readonly embedded?: boolean;
}): React.ReactElement {
  const { copy } = useLanguage();
  const narrow = useMediaQuery('(max-width: 640px)');
  const fieldId = useId();

  const [email, setEmail] = useState('');
  const [storeUrl, setStoreUrl] = useState(defaultStoreUrl);
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);

  const controller = useRef<AbortController | null>(null);
  const storeTouched = useRef(false);

  /* A link attached in the composer after this component mounted should still
     arrive here — but never over something the visitor has typed themselves. */
  useEffect(() => {
    if (!storeTouched.current) setStoreUrl(defaultStoreUrl);
  }, [defaultStoreUrl]);

  /* An in-flight request whose component has gone is a state update on nothing
     and a socket nobody is waiting on. Abort it. */
  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );

  async function onSubmit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    // A second click while the first is in flight would submit twice and race
    // its own response.
    if (status === 'submitting') return;

    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setFieldError(copy.waitlist.errorEmailMissing);
      return;
    }
    if (!looksLikeEmail(trimmed)) {
      setFieldError(copy.waitlist.errorEmailMalformed);
      return;
    }

    setFieldError(undefined);
    setFormError(null);
    setStatus('submitting');

    controller.current?.abort();
    const active = new AbortController();
    controller.current = active;

    const outcome = await submitWaitlist(
      { email: trimmed, storeUrl, website },
      active.signal,
    );

    if (outcome.ok) {
      setStatus('success');
      return;
    }
    // An aborted request means this component is unmounting. Touching state
    // here would be a no-op at best.
    if (outcome.reason === 'aborted') return;

    // Everything the visitor typed survives a failure. Making somebody retype
    // an address because the network dropped is how a retry becomes an exit.
    setStatus('idle');
    setFormError(
      outcome.reason === 'rate_limited'
        ? copy.waitlist.errorRateLimited
        : outcome.reason === 'rejected'
          ? copy.waitlist.errorRejected
          : copy.waitlist.errorNetwork,
    );
  }

  const panel = embedded
    ? {
        position: 'relative' as const,
        background: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        padding: 0,
      }
    : {
        position: 'relative' as const,
        background: 'var(--gradient-charcoal-instrument)',
        border: '1px solid rgba(255,255,255,.08)',
        borderRadius: WAITLIST_PANEL_RADIUS,
        boxShadow: 'var(--elevation-dark-2)',
        padding: narrow ? '24px 20px' : '30px 32px 32px',
      };

  return (
    <div className="mj-rise" style={{ position: 'relative', width: '100%' }}>
      {/*
        The live region announces the outcome that has no other announcement.

        Success replaces the form, so there is no `role="alert"` left on screen
        to carry it. Errors are not repeated here: the field message and the
        form message are both `role="alert"` already, and mirroring them into a
        second region makes a screen reader read every failure twice.

        Mounted at all times rather than rendered with its text — a live region
        added to the DOM at the same moment it gains content is frequently not
        announced at all.
      */}
      <p aria-live="polite" className="mj-visually-hidden">
        {status === 'success'
          ? `${copy.waitlist.successTitle} ${copy.waitlist.successBody}`
          : ''}
      </p>

      {status === 'success' ? (
        <div
          data-surface="landing-charcoal"
          style={{ ...panel, display: 'flex', alignItems: 'flex-start', gap: 15 }}
        >
          <span
            aria-hidden="true"
            style={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--action-primary)',
              boxShadow: 'var(--elevation-action-press)',
            }}
          >
            <Check size={17} strokeWidth={1.5} color="var(--text-on-primary)" />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <strong
              style={{ font: 'var(--type-heading-sm)', color: 'var(--text-primary)' }}
            >
              {copy.waitlist.successTitle}
            </strong>
            <span
              style={{ font: 'var(--type-body-md)', color: 'var(--text-secondary)' }}
            >
              {copy.waitlist.successBody}
            </span>
          </span>
        </div>
      ) : (
        <form
          data-surface="landing-charcoal"
          onSubmit={(event) => void onSubmit(event)}
          noValidate
          style={panel}
        >
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Field
              id={`${fieldId}-email`}
              label={copy.waitlist.emailLabel}
              icon={Mail}
              type="email"
              name="email"
              autoComplete="email"
              required
              placeholder={copy.waitlist.emailPlaceholder}
              value={email}
              error={fieldError}
              wide={narrow}
              onChange={(event) => {
                setEmail(event.target.value);
                if (fieldError !== undefined) setFieldError(undefined);
              }}
            />
            <Field
              id={`${fieldId}-store`}
              label={copy.waitlist.urlLabel}
              hint={copy.waitlist.optional}
              icon={LinkIcon}
              type="url"
              name="store_url"
              autoComplete="url"
              placeholder={copy.waitlist.urlPlaceholder}
              value={storeUrl}
              wide={narrow}
              onChange={(event) => {
                storeTouched.current = true;
                setStoreUrl(event.target.value);
              }}
            />
          </div>

          {/*
            The honeypot. Off-screen rather than `display: none`, because some
            automated fillers skip hidden inputs and this one is meant to be
            filled. `tabIndex={-1}` and `aria-hidden` keep it away from anybody
            navigating by keyboard or screen reader; `autoComplete="off"` keeps
            a browser from helpfully filling it in and locking a real visitor
            out of their own signup.
          */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap',
            }}
          >
            <label htmlFor={`${fieldId}-website`}>{copy.waitlist.honeypotLabel}</label>
            <input
              id={`${fieldId}-website`}
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(event) => {
                setWebsite(event.target.value);
              }}
            />
          </div>

          {copy.waitlist.footnote ? (
            <p
              style={{
                margin: narrow ? '18px 0 0' : '20px 0 0',
                font: 'var(--type-caption)',
                color: 'var(--text-muted)',
                textAlign: 'center',
                textWrap: 'pretty',
              }}
            >
              {copy.waitlist.footnote}
            </p>
          ) : null}

          {formError === null ? null : (
            <p
              role="alert"
              style={{
                margin: '12px 0 0',
                font: 'var(--type-caption)',
                color: 'var(--status-error-fg)',
                textAlign: 'center',
              }}
            >
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            style={{
              width: 'min(100%, 380px)',
              margin: '24px auto 0',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 9,
              height: 48,
              padding: '0 26px',
              borderRadius: WAITLIST_CONTROL_RADIUS,
              border: 'none',
              background:
                status === 'submitting'
                  ? 'var(--action-disabled-surface)'
                  : 'var(--action-primary)',
              color:
                status === 'submitting'
                  ? 'var(--action-disabled-text)'
                  : 'var(--text-on-primary)',
              font: 'var(--type-button-md)',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              cursor: status === 'submitting' ? 'progress' : 'pointer',
              boxShadow: status === 'submitting' ? 'none' : 'var(--elevation-action)',
              transition:
                'transform var(--duration-fast) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard), background var(--duration-fast) var(--ease-standard)',
            }}
          >
            {status === 'submitting' ? copy.waitlist.submitting : copy.waitlist.submit}
            {status === 'submitting' ? null : (
              <ArrowRight
                size={18}
                strokeWidth={1.5}
                className="mj-icon-directional"
                aria-hidden="true"
              />
            )}
          </button>
        </form>
      )}
    </div>
  );
}
